/**
 * Security utility functions for the frontend application
 */

/**
 * Validates if a URL is safe for redirection
 * Prevents open redirect vulnerabilities by checking against whitelist
 * 
 * @param {string} url - The URL to validate
 * @returns {boolean} - True if URL is safe, false otherwise
 */
export const isValidRedirectUrl = (url) => {
    if (!url) return false;

    try {
        // Parse the URL
        const parsedUrl = new URL(url, window.location.origin);

        // Get current origin
        const currentOrigin = window.location.origin;

        // Whitelist of allowed domains for redirect
        const allowedOrigins = [
            currentOrigin,
            // Add your trusted domains here
            // 'https://trusted-domain.com',
        ];

        // Check if the URL origin is in the whitelist
        let isAllowed = allowedOrigins.some(origin =>
            parsedUrl.origin === origin
        );

        // In development, allow any localhost port
        if (!isAllowed && parsedUrl.hostname === 'localhost') {
            isAllowed = true;
        }

        // Additional check: ensure it's not a javascript: or data: URL
        const hasValidProtocol = ['http:', 'https:'].includes(parsedUrl.protocol);

        return isAllowed && hasValidProtocol;
    } catch (error) {
        // If URL parsing fails, it's not valid
        console.error('Invalid URL:', error);
        return false;
    }
};

/**
 * Sanitizes a redirect URL, returning a safe default if invalid
 * 
 * @param {string} url - The URL to sanitize
 * @param {string} defaultUrl - Default URL if validation fails (default: '/dashboard')
 * @returns {string} - Safe URL for redirection
 */
export const sanitizeRedirectUrl = (url, defaultUrl = '/dashboard') => {
    if (!url) return defaultUrl;

    // If it's a relative path starting with /, allow it
    if (url.startsWith('/') && !url.startsWith('//')) {
        // Ensure no protocol smuggling
        return url;
    }

    // For absolute URLs, validate against whitelist
    if (isValidRedirectUrl(url)) {
        return url;
    }

    // If validation fails, return default
    console.warn(`Blocked potentially unsafe redirect to: ${url}`);
    return defaultUrl;
};

/**
 * Validates email format
 * 
 * @param {string} email - Email to validate
 * @returns {boolean} - True if valid email format
 */
export const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

/**
 * Validates password strength
 * 
 * @param {string} password - Password to validate
 * @returns {object} - Validation result with isValid and message
 */
export const validatePasswordStrength = (password) => {
    const minLength = 12;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);

    if (password.length < minLength) {
        return {
            isValid: false,
            message: `Password must be at least ${minLength} characters long`
        };
    }

    if (!hasUpperCase) {
        return {
            isValid: false,
            message: 'Password must contain at least one uppercase letter'
        };
    }

    if (!hasLowerCase) {
        return {
            isValid: false,
            message: 'Password must contain at least one lowercase letter'
        };
    }

    if (!hasNumbers) {
        return {
            isValid: false,
            message: 'Password must contain at least one number'
        };
    }

    if (!hasSpecialChar) {
        return {
            isValid: false,
            message: 'Password must contain at least one special character'
        };
    }

    return {
        isValid: true,
        message: 'Password is strong'
    };
};

/**
 * Sanitizes phone number input
 * 
 * @param {string} phone - Phone number to sanitize
 * @returns {string} - Sanitized phone number (digits only)
 */
export const sanitizePhoneNumber = (phone) => {
    return phone.replace(/\D/g, '');
};

/**
 * Validates phone number format
 * 
 * @param {string} phone - Phone number to validate (digits only)
 * @returns {boolean} - True if valid
 */
export const isValidPhoneNumber = (phone) => {
    // E.164 format: 1-15 digits
    const phoneRegex = /^[1-9]\d{6,14}$/;
    return phoneRegex.test(phone);
};

/**
 * Rate limiter for preventing brute force attacks
 */
class RateLimiter {
    constructor(maxAttempts = 5, windowMs = 15 * 60 * 1000) {
        this.maxAttempts = maxAttempts;
        this.windowMs = windowMs;
        this.attempts = new Map();
    }

    /**
     * Check if action is allowed
     * 
     * @param {string} key - Unique identifier (e.g., email, IP)
     * @returns {object} - { allowed: boolean, remainingAttempts: number, resetTime: Date }
     */
    checkLimit(key) {
        const now = Date.now();
        const record = this.attempts.get(key);

        if (!record) {
            this.attempts.set(key, {
                count: 1,
                firstAttempt: now,
                resetTime: now + this.windowMs
            });
            return {
                allowed: true,
                remainingAttempts: this.maxAttempts - 1,
                resetTime: new Date(now + this.windowMs)
            };
        }

        // Check if window has expired
        if (now > record.resetTime) {
            this.attempts.set(key, {
                count: 1,
                firstAttempt: now,
                resetTime: now + this.windowMs
            });
            return {
                allowed: true,
                remainingAttempts: this.maxAttempts - 1,
                resetTime: new Date(now + this.windowMs)
            };
        }

        // Increment count
        record.count++;

        if (record.count > this.maxAttempts) {
            return {
                allowed: false,
                remainingAttempts: 0,
                resetTime: new Date(record.resetTime)
            };
        }

        return {
            allowed: true,
            remainingAttempts: this.maxAttempts - record.count,
            resetTime: new Date(record.resetTime)
        };
    }

    /**
     * Reset attempts for a key
     * 
     * @param {string} key - Unique identifier
     */
    reset(key) {
        this.attempts.delete(key);
    }

    /**
     * Clear all attempts
     */
    clearAll() {
        this.attempts.clear();
    }
}

// Export singleton instance for login attempts
export const loginRateLimiter = new RateLimiter(5, 15 * 60 * 1000); // 5 attempts per 15 minutes

// Export class for custom instances
export { RateLimiter };
